import React, { ReactElement, useState, useRef } from 'react';
import { 
  Leader, StandardLeaderPermissions,
  OrganizationLeader, LeaderPermission
} from '../../store';
import { IoCloseCircleOutline, IoCreateOutline, IoEllipsisHorizontal } from 'react-icons/io5';
import Luxon from '../Common/Luxon';
import { CabAvatar, CabButton, CabIcon, CabCollapseMenu } from '@CabComponents';
import { CabModal } from '@CabComponents/CabModal';
import PermissionsForm, { PermissionsFormData, PermissionsFormRef } from './PermissionsForm';
import { Box, ListItem, Typography, useTheme } from '@mui/material';
import { getIconSrc } from '../../utils/renderUtils';
import { SharedLeaderGrant } from '../../store/cabinetApi/generated/appCore';
import { ProfileCategory } from '../../store/cabinetApi/generated/profiles';

interface ComponentProps {
  grant: SharedLeaderGrant;
  leader: Leader | OrganizationLeader;
  categories: Record<number, ProfileCategory>;
  revoke: (grantId: number, leaderId: number) => void;
  onEditPermissions?: (grantId: number, permissionsData: PermissionsFormData) => void;
}

type Props = ComponentProps;

export const SharedLeaderItem = ({ grant, leader, categories, revoke, onEditPermissions }: Props): ReactElement => {
  const theme = useTheme();
  const [revokeAlertOpen, setRevokeAlertOpen] = useState(false);
  const [editPermissionsOpen, setEditPermissionsOpen] = useState(false);
  const permissionsFormRef = useRef<PermissionsFormRef>(null);

  const handleOpenRevoke = (): void => {
    setRevokeAlertOpen(true);
  };

  const handleEditPermissions = (): void => {
    setEditPermissionsOpen(true);
  };

  const handleCloseEditPermissions = (): void => {
    setEditPermissionsOpen(false);
  };

  const handleSavePermissions = (permissionsData: PermissionsFormData): void => {
    if (onEditPermissions) {
      onEditPermissions(grant.id, permissionsData);
    }
    setEditPermissionsOpen(false);
  };

  const handleRevoke = (): void => {
    setRevokeAlertOpen(false);
    revoke(grant.id, leader.id);
  };

  const basicPermissionDisplayMap: {[K in keyof StandardLeaderPermissions]: string} = {
    basic: 'Basic Info',
    airlines: 'Airlines',
    hotels: 'Hotels',
    personal: 'Family'
  };

  const editCategories: string[] = [];
  const viewCategories: string[] = [];
  Object.keys(basicPermissionDisplayMap).forEach(category => {
    const categoryIndex = category as keyof StandardLeaderPermissions;
    const categoryPermission = grant.permissions[categoryIndex];
    if (categoryPermission.edit) {
      editCategories.push(basicPermissionDisplayMap[categoryIndex]);
    } else if (categoryPermission.view) {
      viewCategories.push(basicPermissionDisplayMap[categoryIndex]);
    }
  });

  Object.values(categories).forEach(category => {
    if (category.id in grant.category_permissions) {
      const categoryPermission = grant.category_permissions[category.id];
      if (categoryPermission.edit) {
        editCategories.push(category.title || '');
      } else if (categoryPermission.view) {
        viewCategories.push(category.title || '');
      }
    }
  });


  const hasViewEditSchedulingPerms = Object.keys(grant.scheduling_permissions)
    .some(
      k => ['view', 'edit', 'create', 'delete'].includes(k) 
      && grant.scheduling_permissions[k as keyof LeaderPermission]
    );

  const hasOnlyAddToMeetingSchedulingPerms = !!grant.scheduling_permissions.add_to_meeting;

  const crmContactPerms = grant.crm_contact_permissions || {};
  const hasCrmViewEditContacts = !!(crmContactPerms.view && crmContactPerms.change);

  return (
    <>
      <ListItem divider>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%"
          }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between"
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CabAvatar
                name={`${grant.to_user.first_name} ${grant.to_user.last_name}`}
                src={getIconSrc(grant.to_user.profile.pic_url || undefined)}
                color={theme.palette.primary.main}
                sx={{ marginRight: 1, width: '40px', height: '40px', fontSize: '16px' }}
              />
              <Typography>{grant.to_user.first_name + ' ' + grant.to_user.last_name}</Typography>
            </Box>
            <CabCollapseMenu
              target={
                <CabIcon 
                  Icon={IoEllipsisHorizontal} 
                  alt='Menu' 
                  sx={{ cursor: 'pointer', padding: 1 }}
                />
              }
              buttons={
                <>
                  <CabButton
                    buttonType='text'
                    onClick={handleEditPermissions}
                    icon={<CabIcon Icon={IoCreateOutline} alt='Edit Permissions'/>}
                    sx={{ justifyContent: 'flex-start', width: '100%' }}
                    size="large"
                  >
                    Edit Permissions
                  </CabButton>
                  <CabButton
                    buttonType='text'
                    color='error'
                    onClick={handleOpenRevoke}
                    icon={<CabIcon Icon={IoCloseCircleOutline} alt='Revoke'/>}
                    sx={{ justifyContent: 'flex-start', width: '100%' }}
                    size="large"
                  >
                    Revoke
                  </CabButton>
                </>
              }
              popOverTitle="Grant Actions"
            />
          </Box>
          <Box sx={{
            display: "flex"
          }}>
            <Box
              sx={{
                display: "flex",
                flex: 1,
                gap: 1
              }}>
              <Typography variant="subtitle2">Starting on: </Typography>
              <Luxon format="MM/dd/yyyy" date={grant.start_date || ''} />
            </Box>
            {grant.end_date &&
              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  gap: 1
                }}>
                <Typography variant="subtitle2">Ending on: </Typography>
                <Luxon format="MM/dd/yyyy" date={grant.end_date || ''} />
              </Box>
            }
          </Box>
          {grant.is_admin ? (
            <Box style={{ display: 'flex' }}>
              <Typography>All Permissions Granted</Typography>
            </Box>
          ) : (
            <>
              <Box style={{ display: 'flex' }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1
                  }}>
                  <Typography variant="subtitle2">Scheduling:</Typography>
                  <Typography>
                    {hasViewEditSchedulingPerms 
                      ? 'View/Edit all meetings' 
                      : hasOnlyAddToMeetingSchedulingPerms 
                        ? 'Only add to meetings' 
                        : 'No'}
                  </Typography>
                </Box>
              </Box>
              <Box style={{ display: 'flex' }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1
                  }}>
                  <Typography variant="subtitle2">CRM:</Typography>
                  <Typography>
                    {hasCrmViewEditContacts ? 'View/Edit all contacts' : 'No'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flex: 1
                  }}>
                  <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{
                      textOverflow: "unset",
                      overflow: "visible"
                    }}>View-Only:</Typography>
                  <Typography>
                    {viewCategories.join(', ')}
                    {viewCategories.length < 1 && 'None'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flex: 1
                  }}>
                  <Typography variant="subtitle2">Edit:</Typography>
                  <Typography>
                    {editCategories.join(', ')}
                    {editCategories.length < 1 && 'None'}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </ListItem>
      <CabModal
        open={revokeAlertOpen}
        onClose={(): void => setRevokeAlertOpen(false)}
        title={'Revoke Access'}
        text={`Are you sure you want to revoke access to ${leader.first_name} ${leader.last_name}'s ` +
        `profile from ${grant.to_user.first_name} ${grant.to_user.last_name}?`}
        isAlert={true}
        noFullScreen={true}
        actionButtons={
          <>
            <CabButton buttonType='secondary' onClick={() => setRevokeAlertOpen(false)}>
              Cancel
            </CabButton>
            <CabButton onClick={handleRevoke}>
              Revoke
            </CabButton>
          </>
        }
      />
      <CabModal
        open={editPermissionsOpen}
        onClose={handleCloseEditPermissions}
        title={`Edit Permissions for ${grant.to_user.first_name} ${grant.to_user.last_name}`}
        closeIcon
        closeOnBackdropClick
        actionButtons={
          <>
            <CabButton buttonType='secondary' onClick={handleCloseEditPermissions}>
              Back
            </CabButton>
            <CabButton onClick={() => {
              permissionsFormRef.current?.submit();
            }}>
              Share
            </CabButton>
          </>
        }
        sx={{ '& .MuiPaper-root': { width: 600, height: 900 } }}
      >
        <Box sx={{ padding: 2 }}>
          <PermissionsForm
            ref={permissionsFormRef}
            grant={grant}
            leaderProfileCategories={categories}
            showUserSelector={false}
            showDateRange={true}
            onSubmit={handleSavePermissions}
            onCancel={handleCloseEditPermissions}
          />
        </Box>
      </CabModal>
    </>
  );
};

export default SharedLeaderItem;